import React from 'react';
import * as Form from 'react-hook-form';
import { Keyboard, Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';
import { t } from '@lingui/macro';
import { useRoute } from '@react-navigation/native';

import RecoverySetNewPassword from '@src/api/RecoverySetNewPassword';
import { passwordRegex } from '@src/helpers';
import useAuthenticationTokens from '@src/hooks/useAuthenticationTokens';
import useSnackBar from '@src/hooks/useSnackBar';
import { i18n, i18yup } from '@src/i18n';

import { FormData, ScreenRouteProp } from './types';

const useForm = () => {
  const route = useRoute<ScreenRouteProp>();
  const [loading, setLoading] = React.useState(false);
  const { setTokens } = useAuthenticationTokens();
  const snackBar = useSnackBar();
  const form = Form.useForm<FormData>({
    validationSchema: i18yup.object<FormData>({
      password: i18yup
        .string()
        .matches(passwordRegex, i18n._(t`Invalid password`))
        .required()
        .label('Password'),
      retypePassword: i18yup
        .string()
        .matches(passwordRegex, i18n._(t`Invalid password`))
        .required()
        .label('Retype Password'),
    }),
  });

  const handleSubmit = React.useCallback(
    async ({ password, retypePassword }: FormData) => {
      if (password !== retypePassword) {
        snackBar.show({ content: i18n._(t`Passwords not match`) });
        return;
      }

      setLoading(true);
      try {
        Keyboard.dismiss();
        const response = await RecoverySetNewPassword({
          device: DeviceInfo.getDeviceId(),
          deviceId: DeviceInfo.getUniqueId(),
          os: Platform.OS,
          preToken: route.params.preToken,
          token: route.params.token,
          osVersion: DeviceInfo.getReadableVersion(),
          password,
        });

        if (response.data.result === 'SUCCESS') {
          const accessToken = response.headers['x-auth-token'];
          const { refreshToken, region, userId, setupComplete } = response.data;
          if (accessToken) {
            setTokens({
              userID: userId,
              refreshToken,
              accessToken,
              region,
              setupComplete,
            });
          }
        }
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    },
    [route.params.preToken, route.params.token, setTokens, snackBar]
  );

  return React.useMemo(
    () => ({
      form,
      handleSubmit: form.handleSubmit(handleSubmit),
      loading,
    }),
    [form, handleSubmit, loading]
  );
};

export default useForm;
